import { useState } from "react";
import { api } from "../../lib/api";
import { t } from "../../lib/i18n";
import { toast } from "sonner";
import { Field, Modal } from "./_shared";

export default function PlayersAdmin({ players, refresh }) {
  const [form, setForm] = useState(null);
  const empty = { handle: "", real_name: "", country: "JP", bio: "", avatar_url: "" };

  const save = async () => {
    try {
      if (form.id) await api.put(`/admin/players/${form.id}`, form);
      else await api.post(`/admin/players`, form);
      toast.success(t.saved);
      setForm(null);
      refresh();
    } catch (_) { toast.error(t.failed); }
  };
  const remove = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    await api.delete(`/admin/players/${id}`);
    toast.success(t.deleted);
    refresh();
  };

  const purge = async (id, handle) => {
    // Iter 41 — hard purge: user account + player + decks + registrations.
    // Two confirms because it's irreversible and touches auth records.
    if (!window.confirm(t.confirmPurgePlayer.replace("{n}", handle))) return;
    if (!window.confirm(t.confirmPurgePlayerFinal)) return;
    try {
      const r = await api.delete(`/admin/players/${id}/purge`);
      const d = r.data?.deleted || {};
      toast.success(`${t.playerPurged}: ${d.users || 0}u · ${d.decks || 0}d · ${d.registrations || 0}r`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.failed);
    }
  };

  const resetOne = async (id, handle) => {
    if (!window.confirm(t.confirmResetStats.replace("{n}", handle))) return;
    try {
      await api.post(`/admin/players/${id}/reset-stats`);
      toast.success(t.statsReset);
      refresh();
    } catch { toast.error(t.failed); }
  };

  const resetAll = async () => {
    if (!window.confirm(t.confirmResetAllStats)) return;
    if (!window.confirm(t.confirmResetAllStatsDoubleCheck)) return;
    try {
      const r = await api.post(`/admin/players/reset-stats`);
      toast.success(`${t.statsResetAll}: ${r.data.modified}`);
      refresh();
    } catch { toast.error(t.failed); }
  };

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setForm(empty)} data-testid="new-player-btn"
          className="px-5 py-2.5 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">
          {t.newPlayer}
        </button>
        <button onClick={resetAll} data-testid="reset-all-stats-btn"
          className="ml-auto px-4 py-2.5 border border-[#FF0055]/40 text-[#FF0055] hover:bg-[#FF0055]/10 font-bold uppercase tracking-[0.25em] text-[10px]">
          ⚠ {t.resetAllStats}
        </button>
      </div>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {players.map((p) => (
          <div key={p.id} className="border border-white/10 bg-[#0C0C14] p-4 flex justify-between items-start">
            <div className="min-w-0">
              <div className="font-display font-bold uppercase italic text-xl truncate">{p.handle}</div>
              <div className="text-xs text-white/50 mt-1 font-mono">
                {p.country} · {p.points} {t.pts} · {p.wins || 0}W-{p.losses || 0}L
              </div>
            </div>
            <div className="flex flex-col gap-1 text-[10px] uppercase tracking-[0.25em] font-bold flex-shrink-0">
              <button onClick={() => setForm(p)} className="text-white/70 hover:text-[#00E5FF]">{t.edit}</button>
              <button
                onClick={() => resetOne(p.id, p.handle)}
                data-testid={`player-row-${p.id}-reset-stats`}
                className="text-[#E2FF00]/80 hover:text-[#E2FF00]"
                title={t.resetStats}
              >
                ⚡ {t.reset}
              </button>
              <button onClick={() => remove(p.id)} className="text-[#FF0055]">{t.delete}</button>
              <button
                onClick={() => purge(p.id, p.handle)}
                data-testid={`player-row-${p.id}-purge`}
                className="text-[#FF0055] font-black hover:text-white hover:bg-[#FF0055] px-1"
                title={t.purgePlayerTitle}
              >
                ☠ {t.purge}
              </button>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <Modal title={form.id ? t.editPlayer : t.newPlayer} onClose={() => setForm(null)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t.handle} value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} />
            <Field label={t.realName} value={form.real_name || ""} onChange={(v) => setForm({ ...form, real_name: v })} />
            <Field label={t.country} value={form.country} onChange={(v) => setForm({ ...form, country: v.toUpperCase() })} />
            <Field label={t.avatarUrl} value={form.avatar_url || ""} onChange={(v) => setForm({ ...form, avatar_url: v })} />
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40">{t.bio}</label>
              <textarea rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                className="w-full bg-transparent border border-white/15 px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#00E5FF]" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setForm(null)} className="px-4 py-2 text-xs uppercase tracking-[0.25em] font-bold text-white/60">{t.cancel}</button>
            <button onClick={save} data-testid="save-player-btn"
              className="px-5 py-2 bg-[#FF0055] hover:bg-[#FF3377] text-white font-bold uppercase tracking-[0.25em] text-xs">{t.save}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
